import React, { useState, useEffect } from 'react'
import Avatar from 'particles/Avatar'
import TextArea from 'particles/TextInputs/TextArea'
import RectangleButton from 'particles/Button/RectangleButton'
import IconButton from 'particles/Button/IconButton'
import styled from 'particles/themes/styled'
import Text from 'particles/Text'

interface ContainerProps {
    width?: number
    isCommentItem?: boolean
    maxTextAreaHeight?: number
}
const Container = styled.div<ContainerProps>`
    width: ${({ width }) => width + 'px'};
    max-width: ${({ width }) => width + 'px'};
    display: flex;
    gap: ${({ theme }) => theme.spacing[100]};
    .avatar {
        margin-top: ${({ isCommentItem, theme }) => (isCommentItem ? 0 : theme.spacing[200])};
    }

    .default-comment {
        max-width: 100%;
        word-break: break-word;
        white-space: pre-line;
    }
    .comment-info {
        width: 100%;
        max-width: 100%;
        display: flex;
        align-items: center;
        gap: ${({ theme }) => theme.spacing[100]};
        justify-content: space-between;
        margin-bottom: ${({ theme }) => theme.spacing[100]};
        .first-block {
            max-width: calc(100% - 20px);
            display: flex;
            align-items: center;
            gap: ${({ theme }) => theme.spacing[100]};
        }
        .profile-name {
            flex: 1;
            max-width: 100%;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
        }
        .more-action {
            min-width: 20px;
        }
    }
    .comment-block {
        display: flex;
        flex-direction: column;
        width: 100%;
        box-sizing: border-box;
    }
    .button-groups {
        width: fit-content;
        align-self: flex-end;
        display: flex;
        justify-content: flex-end;
        gap: ${({ theme }) => theme.spacing[200]};
        margin-top: ${({ theme, isCommentItem }) => (isCommentItem ? theme.spacing[100] : theme.spacing[200])};
    }
`

interface AddCommentProps {
    className?: string
    profile: { uuid: string; name: string }
    width?: number
    minTextAreaHeight?: number
    maxTextAreaHeight?: number
    placeholder?: string
    primaryButtonLabel?: string
    secondaryButtonLabel?: string
    isCommentItem?: boolean
    timeSlots?: string
    isEditable?: boolean
    value?: string
    onPrimaryButtonClick: (comment?: string) => void
    onSecondaryButtonClick?: (comment?: string) => void
    moreActionCallback?: (e: React.MouseEvent<HTMLElement>, onEditModeCallback: () => void) => void
    hidableButtons?: boolean
    onFocusCallBack?: () => void
    onCommentChange?: (comment?: string) => void
    isLoading?: boolean
    autoClearComment?: boolean
    hideReadOnlyText?: boolean
}

const AddComment: React.FC<AddCommentProps> = ({
    className,
    profile,
    width = 240,
    minTextAreaHeight = 40,
    maxTextAreaHeight = 400,
    primaryButtonLabel,
    secondaryButtonLabel,
    placeholder,
    isCommentItem = false,
    isEditable = true,
    timeSlots,
    value = '',
    onPrimaryButtonClick,
    onSecondaryButtonClick,
    moreActionCallback,
    hidableButtons = false,
    onFocusCallBack,
    onCommentChange,
    isLoading,
    autoClearComment = true,
    hideReadOnlyText,
}) => {
    const [comment, setComment] = useState(value)
    const [showButtons, setShowButtons] = useState(!hidableButtons)
    //total width - (avatar + gap)
    const textareaWidth = width - 28

    useEffect(() => {
        if (onCommentChange) {
            onCommentChange(comment)
        }
    }, [comment])

    useEffect(() => {
        setShowButtons(!hidableButtons)
    }, [hidableButtons])

    useEffect(() => {
        setComment(value)
    }, [value])

    return (
        <Container
            width={width}
            isCommentItem={isCommentItem}
            maxTextAreaHeight={maxTextAreaHeight}
            className={className}
        >
            <Avatar profile={profile} className="avatar" />
            <div className="comment-block">
                {isCommentItem && (
                    <div className="comment-info">
                        <div className="first-block">
                            <Text scale="bodyS700" className="profile-name">
                                {profile.name}
                            </Text>
                            {timeSlots && (
                                <Text scale="caption400" color="secondary">
                                    {timeSlots}
                                </Text>
                            )}
                        </div>
                        {moreActionCallback && (
                            <IconButton
                                icon="dotsThree"
                                form="icon"
                                size="xs"
                                color="neutral"
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                    moreActionCallback(e, () => {
                                        if (isEditable) {
                                            setShowButtons(true)
                                        }
                                    })
                                }}
                                className="more-action"
                            />
                        )}
                    </div>
                )}
                <TextArea
                    size="m"
                    width={textareaWidth}
                    minTextAreaHeight={minTextAreaHeight}
                    placeholder={placeholder}
                    maxTextAreaHeight={maxTextAreaHeight}
                    value={comment}
                    onChange={(value: string) => {
                        setComment(value)
                    }}
                    isDisabled={isLoading}
                    onFocusCallBack={(isFocus: boolean) => {
                        if (isFocus) {
                            setShowButtons(isFocus)
                        }
                        if (onFocusCallBack) {
                            onFocusCallBack()
                        }
                    }}
                    isReadOnly={isCommentItem && !showButtons}
                    isCardItem
                    autoFocus={isCommentItem}
                    hideReadOnlyText={hideReadOnlyText}
                />

                {showButtons && (
                    <div className="button-groups">
                        {hidableButtons && (
                            <RectangleButton
                                label={secondaryButtonLabel}
                                loadingText={primaryButtonLabel}
                                size="s"
                                form="text"
                                color="neutral"
                                type="button"
                                onClick={() => {
                                    setComment(value)
                                    if (onSecondaryButtonClick) {
                                        onSecondaryButtonClick(comment)
                                    }
                                    setShowButtons(false)
                                }}
                            />
                        )}
                        <RectangleButton
                            label={primaryButtonLabel}
                            loadingText={primaryButtonLabel}
                            size="s"
                            form="filled"
                            color="neutral"
                            isLoading={isLoading}
                            type="button"
                            onClick={() => {
                                onPrimaryButtonClick(comment)
                                if (hidableButtons) {
                                    setShowButtons(false)
                                }
                                if (!isCommentItem && autoClearComment) {
                                    setComment('')
                                }
                            }}
                        />
                    </div>
                )}
            </div>
        </Container>
    )
}

export default AddComment
